if (!customElements.get('blankabric-drop-announcement')) {
  customElements.define('blankabric-drop-announcement', class extends HTMLElement {
    connectedCallback() {
      this.cleanup?.();
      const abort = new AbortController();
      let timer = 0;
      if (this.dataset.offerEndsAt) {
        const deadline = Date.parse(this.dataset.offerEndsAt);
        if (!Number.isFinite(deadline)) { this.hidden = true; return; }
        const serverTime = Number(this.dataset.serverNow) * 1000;
        this.offerOrigin ??= Number.isFinite(serverTime) && serverTime > 0 ? serverTime : Date.now();
        this.offerStarted ??= performance.now();
        const update = () => {
          clearTimeout(timer);
          const remaining = deadline - this.offerOrigin - (performance.now() - this.offerStarted);
          this.hidden = remaining <= 0;
          if (remaining > 0) timer = setTimeout(update, Math.min(remaining, 2147483647));
        };
        this.cleanup = () => { abort.abort(); clearTimeout(timer); };
        update();
        document.addEventListener('visibilitychange', update, { signal: abort.signal });
        return;
      }
      const stopDisclosure = this.setupDisclosure(abort.signal);
      this.cleanup = () => { abort.abort(); clearInterval(timer); stopDisclosure(); };
      const deadline = Date.parse(this.dataset.dropAt);
      const clock = this.querySelector('[data-drop-clock]');
      if (!Number.isFinite(deadline) || !clock) return;
      const locale = document.documentElement.lang || 'fr';
      const zone = { timeZone: 'Europe/Paris' };
      const day = new Intl.DateTimeFormat(locale, { ...zone, day: 'numeric', month: 'long' });
      const minute = new Intl.DateTimeFormat('en', { ...zone, minute: 'numeric' }).format(deadline);
      const hour = new Intl.DateTimeFormat(locale, { ...zone, hour: 'numeric', ...(Number(minute) ? { minute: '2-digit' } : {}) });
      this.querySelectorAll('[data-drop-date]').forEach(node => { node.textContent = `${day.format(deadline)} · ${hour.format(deadline)}`; });
      const dayLabel = new Intl.NumberFormat(locale, { style: 'unit', unit: 'day', unitDisplay: 'narrow' }).formatToParts(2).find(part => part.type === 'unit')?.value;
      this.querySelector('[data-drop-days-short]').textContent = dayLabel ? ` ${dayLabel}` : '';
      const serverTime = Number(this.dataset.serverNow) * 1000;
      // Keep the original clock across theme-editor reconnects, regardless of the device clock.
      this.clockOrigin ??= Number.isFinite(serverTime) && serverTime > 0 ? serverTime : Date.now();
      this.clockStarted ??= performance.now();
      const tick = () => {
        const seconds = Math.max(0, Math.ceil((deadline - this.clockOrigin - (performance.now() - this.clockStarted)) / 1000));
        const units = { days: Math.floor(seconds / 86400), hours: Math.floor(seconds / 3600) % 24, minutes: Math.floor(seconds / 60) % 60, seconds: seconds % 60 };
        for (const [unit, value] of Object.entries(units)) this.querySelector(`[data-drop-unit="${unit}"]`).textContent = String(value).padStart(2, '0');
        clock.hidden = seconds === 0;
        this.querySelector('[data-drop-waiting]').hidden = seconds > 0;
        if (seconds === 0) { clearInterval(timer); timer = 0; }
        return seconds;
      };
      if (tick() > 0) timer = setInterval(tick, 1000);
      document.addEventListener('visibilitychange', () => { if (!document.hidden) tick(); }, { signal: abort.signal });
    }

    setupDisclosure(signal) {
      const details = this.querySelector('details');
      const summary = details?.querySelector('summary');
      const body = details?.querySelector('.bb-drop-announcement__body');
      if (!summary || !body) return () => {};
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let animation = null;
      let expanded = details.open;
      const cancel = () => {
        if (!animation) return;
        animation.onfinish = null;
        animation.cancel();
        animation = null;
      };
      const finish = () => {
        cancel();
        details.open = expanded;
        body.inert = !expanded;
      };
      const setExpanded = (next, restoreFocus = false) => {
        const wasOpen = details.open;
        const current = wasOpen ? getComputedStyle(body) : null;
        const offset = this.classList.contains('bb-home-hero__drop') ? 'translateY(8px)' : 'translateY(-8px)';
        const fromOpacity = current ? current.opacity || '1' : '0';
        const fromTransform = current ? current.transform || 'translateY(0px)' : offset;
        expanded = next;
        cancel();
        if (restoreFocus) summary.focus({ preventScroll: true });
        body.inert = !expanded;
        if (motion.matches || typeof body.animate !== 'function') { finish(); return; }
        details.open = true;
        // Only the absolutely positioned panel moves; the banner and catalogue keep their height.
        animation = body.animate(
          { opacity: [fromOpacity, expanded ? '1' : '0'], transform: [fromTransform, expanded ? 'translateY(0px)' : offset] },
          { duration: 260, easing: 'cubic-bezier(.22, 1, .36, 1)', fill: 'both' }
        );
        animation.onfinish = finish;
      };
      summary.addEventListener('click', event => {
        if (event.defaultPrevented) return;
        event.preventDefault();
        setExpanded(animation ? !expanded : !details.open);
      }, { signal });
      const hasActiveModal = () => [...document.querySelectorAll('dialog[open], [aria-modal="true"]')].some(modal => {
        if (modal.closest('[hidden], [aria-hidden="true"], [inert], dialog:not([open])')) return false;
        return modal.matches('dialog[open]') || modal.getClientRects().length > 0;
      });
      document.addEventListener('pointerdown', event => {
        if (!details.open || !expanded || this.contains(event.target)) return;
        // Keep the signup dialog's return target available while it is in use.
        if (hasActiveModal()) return;
        setExpanded(false);
      }, { signal });
      document.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || event.defaultPrevented || !details.open || !expanded) return;
        if (hasActiveModal()) return;
        event.preventDefault();
        setExpanded(false, true);
      }, { signal });
      details.addEventListener('toggle', () => {
        if (!animation) {
          expanded = details.open;
          body.inert = !expanded;
        }
      }, { signal });
      motion.addEventListener('change', () => { if (motion.matches && animation) finish(); }, { signal });
      window.addEventListener('resize', () => { if (animation) finish(); }, { signal });
      return () => { if (!animation) expanded = details.open; finish(); };
    }
    disconnectedCallback() { this.cleanup?.(); }
  });
}
